/**
 * queryKey 표준 빌더 — 모든 list/detail query 의 react-query key 를 일관된 형태로.
 *
 * 형식:
 *   list   : [domain, "list", tab, filters?]
 *   detail : [domain, "detail", id]
 *   all    : [domain]                       (invalidate 전체 도메인)
 *
 * 사용 예:
 *   listKey("gifts", "sent")                          // ["gifts", "list", "sent"]
 *   listKey("kuji", "new", { ipBrandIds: [1,2] })    // ["kuji", "list", "new", { ipBrandIds: [1,2] }]
 *   detailKey("kuji", 123)                            // ["kuji", "detail", 123]
 *   domainKey("gifts")                                // ["gifts"]
 *
 * invalidate 정책:
 *   queryClient.invalidateQueries({ queryKey: domainKey("gifts") })
 *     → 같은 도메인의 모든 탭/디테일 query 한 번에 invalidate.
 */

/** 도메인 식별자 — 신규 도메인 추가 시 여기에 string literal 로만 추가. */
export type QueryDomain =
  | "gifts"
  | "cards"
  | "cardsets"
  | "collections"
  | "kuji"
  | "kuji-home"
  | "gacha"
  | "orders"
  | "shipments"
  | "returns"
  | "wishlists"
  | "search"
  | "support"
  | "notifications"
  | "banners"
  | "showcase";

/** 탭 분기가 없는 단일 리스트의 placeholder tab. */
export const NO_TAB = "_" as const;

export type Filters = Record<string, unknown> | undefined;

/**
 * List query key. tab 이 없으면 NO_TAB 을 전달. filters 가 있으면 객체로 직렬화.
 *
 * react-query 는 array deep-equality 로 key 를 구분하므로 객체 안 필드 순서는
 * 무관하지만, 객체 reference 가 매 렌더마다 바뀌면 key 도 새로워진다.
 * useMemo 또는 stable filters object 사용 권장.
 */
/**
 * 도메인의 모든 list query 를 prefix 매칭하는 key.
 *
 * react-query 는 array prefix 로 invalidate 하므로 `[domain,"list"]` 하나로 해당
 * 도메인의 모든 탭/필터 list query 를 한 번에 무효화할 수 있다 (detail 은 prefix 가
 * 달라 제외).
 *
 * listKey 와 동일한 `[domain,"list",...]` 구조의 단일 출처 — 한쪽만 바뀌어 invalidate
 * 가 조용히 빗나가는 것을 막는다. (drift 가드 테스트: queryKeys.test.ts)
 */
export function listDomainPrefix(domain: QueryDomain): readonly [QueryDomain, "list"] {
  return [domain, "list"] as const;
}

export function listKey(
  domain: QueryDomain,
  tab: string,
  filters?: Filters,
): readonly unknown[] {
  const prefix = listDomainPrefix(domain);
  return filters ? [...prefix, tab, filters] as const : [...prefix, tab] as const;
}

/** Detail query key — 단건 조회용. */
export function detailKey(domain: QueryDomain, id: number | string): readonly unknown[] {
  return [domain, "detail", id] as const;
}

/** 도메인 전체 invalidate 용. */
export function domainKey(domain: QueryDomain): readonly unknown[] {
  return [domain] as const;
}
